/*!
 * Copyright © 2026 Nail Alliance - North America, Inc.
 * Authors: See snippets/copyright-notices.liquid in the theme.
 * No other use is authorized without a written contract.
 */

(() => {
  const cache = new Map();

  const getData = (url) => {
    if (!cache.has(url)) {
      cache.set(url, fetch(url, { headers: { Accept: 'application/json' } }).then((response) => {
        if (!response.ok) throw new Error(`Collection data request failed: ${response.status}`);
        return response.json();
      }));
    }
    return cache.get(url);
  };

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  })[character]);

  const mediaUrl = (origin, value) => {
    if (!value) return '';
    if (/^https?:\/\//.test(value)) return value;
    return `${String(origin || 'https://gelish.com').replace(/\/$/, '')}/${String(value).replace(/^\//, '')}`;
  };

  const picture = (origin, image, alt, className = '') => {
    if (!image) return '';
    const small = mediaUrl(origin, image.img || image.img2x || image.img3x);
    const medium = mediaUrl(origin, image.img2x || image.img || image.img3x);
    const large = mediaUrl(origin, image.img3x || image.img2x || image.img);
    return `<picture class="${className}">
      <source media="(max-width: 520px)" srcset="${escapeHtml(small)}">
      <source media="(max-width: 980px)" srcset="${escapeHtml(medium)}">
      <img src="${escapeHtml(large)}" alt="${escapeHtml(alt)}" loading="lazy">
    </picture>`;
  };

  const collectionUrl = (season) => `/pages/colors-collections-${encodeURIComponent(season)}`;

  class ColorCollectionsIndex extends HTMLElement {
    connectedCallback() {
      this.render().catch(() => {
        this.innerHTML = '<p class="color-collections-error">Collections could not be loaded.</p>';
      });
    }

    disconnectedCallback() {
      window.clearInterval(this.carouselTimer);
      window.clearInterval(this.promoTimer);
    }

    async render() {
      const data = await getData(this.dataset.source);
      const collections = data.collections || [];
      const latest = collections.find((item) => item.season === this.dataset.latestSeason) || collections[0];
      if (!latest) return;
      const origin = this.dataset.assetOrigin;
      const latestName = latest.displayName || latest.name;
      const latestMedia = latest.season === 'fall-2026'
        ? picture(origin, latest.squareImage, `${latestName} collection`, 'color-collections-latest__media-item')
        : `<video class="color-collections-latest__video" autoplay muted loop playsinline poster="${escapeHtml(mediaUrl(origin, latest.squareImage?.img3x))}">
            <source src="${escapeHtml(mediaUrl(origin, '/videos/collection/summer_2026/22755-GEL-SUMMER-2026-Keep-It-Juicy-Video-Hand-Glam-GEL-FNL.mp4'))}" type="video/mp4">
          </video>`;
      const carousel = latest.season === 'fall-2026'
        ? latest.carouselImages
        : [
            { img: '/images/collections/main_collection/summer-2026/22756-GEL-SM26-KeepItJuicy-Photo-Group.webp', img2x: '/images/collections/main_collection/summer-2026/22756-GEL-SM26-KeepItJuicy-Photo-Group@2x.webp', img3x: '/images/collections/main_collection/summer-2026/22756-GEL-SM26-KeepItJuicy-Photo-Group@3x.webp' },
            latest.squareImage,
            { img: '/images/collections/main_collection/summer-2026/22756-GEL-SM26-KeepItJuicy-Photo-Group3.webp', img2x: '/images/collections/main_collection/summer-2026/22756-GEL-SM26-KeepItJuicy-Photo-Group3@2x.webp', img3x: '/images/collections/main_collection/summer-2026/22756-GEL-SM26-KeepItJuicy-Photo-Group3@3x.webp' }
          ];

      this.innerHTML = `
        <div class="color-collections-index__inner">
          <section class="color-collections-latest" aria-labelledby="LatestCollectionTitle">
            <a class="color-collections-latest__media" href="${collectionUrl(latest.season)}">${latestMedia}</a>
            <div class="color-collections-latest__copy">
              <h1 id="LatestCollectionTitle"><strong>Explore Our</strong><br>Newest Collection</h1>
              <a class="color-collections-latest__cta" href="${collectionUrl(latest.season)}">
                <span>Go to ${escapeHtml(latestName)}</span><span aria-hidden="true">→</span>
              </a>
            </div>
            <a class="color-collections-latest__card" href="${collectionUrl(latest.season)}">
              ${picture(origin, latest.productGlamShot || latest.squareImage, latestName)}
              <span>${escapeHtml(latest.season.replace('-', ' '))}</span>
            </a>
            <div class="color-collections-latest__carousel" data-collection-carousel>
              ${carousel.filter(Boolean).map((image, index) => `<div class="color-collections-latest__slide${index === 0 ? ' is-active' : ''}" data-collection-slide>${picture(origin, image, latestName)}</div>`).join('')}
            </div>
          </section>

          <section class="color-collections-archive" aria-labelledby="MoreCollectionsTitle">
            <h2 id="MoreCollectionsTitle">More Collections</h2>
            <div class="color-collections-archive__grid">
              ${collections.filter((item) => item.season !== latest.season).map((item) => `
                <a class="color-collections-card" href="${collectionUrl(item.season)}">
                  ${picture(origin, item.squareImage, item.name)}
                  <span><strong>${escapeHtml(item.season.split('-')[0])}</strong>${escapeHtml(item.season.split('-')[1])}</span>
                </a>
              `).join('')}
            </div>
          </section>
        </div>
        <div class="color-collections-promos" data-collection-promos>
          ${[
            ['/nail-arts#chrome-stix', '/images/banner/flex_foundation_banner.webp', '/images/banner/flex_foundation_banner@2x.webp', '/images/banner/flex_foundation_banner@3x.webp'],
            ['/gelish#essentials', '/images/banner/instant_chrome_banner.webp', '/images/banner/instant_chrome_banner@2x.webp', '/images/banner/instant_chrome_banner@3x.webp'],
            ['/nail-lamps-tools/e-files', '/images/banner/iq_banner.webp', '/images/banner/iq_banner@2x.webp', '/images/banner/iq_banner@3x.webp']
          ].map(([href, img, img2x, img3x], index) => `
            <a href="${href}" class="color-collections-promos__slide${index === 1 ? ' is-active' : ''}" data-promo-slide>
              ${picture(origin, { img, img2x, img3x }, href)}
            </a>`).join('')}
        </div>`;

      const slides = [...this.querySelectorAll('[data-collection-slide]')];
      if (slides.length > 1) {
        let active = 0;
        this.carouselTimer = window.setInterval(() => {
          slides[active].classList.remove('is-active');
          active = (active + 1) % slides.length;
          slides[active].classList.add('is-active');
        }, 3000);
      }
      const promos = [...this.querySelectorAll('[data-promo-slide]')];
      if (promos.length > 1) {
        let activePromo = 1;
        this.promoTimer = window.setInterval(() => {
          promos[activePromo].classList.remove('is-active');
          activePromo = (activePromo + 1) % promos.length;
          promos[activePromo].classList.add('is-active');
        }, 2500);
      }
    }
  }

  class ColorCollectionPage extends HTMLElement {
    connectedCallback() {
      this.catalogTemplate = this.querySelector('[data-product-catalog]');
      this.productNodes = new Map();
      this.catalogTemplate?.content.querySelectorAll('[data-color-product]').forEach((node) => {
        this.productNodes.set(node.dataset.sku, node);
      });
      this.render().catch(() => {
        this.innerHTML = '<p class="color-collections-error">This collection could not be loaded.</p>';
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    async render() {
      const data = await getData(this.dataset.source);
      const isLocalPreview = ['127.0.0.1', 'localhost'].includes(window.location.hostname);
      const previewSeason = isLocalPreview
        ? new URLSearchParams(window.location.search).get('preview_season')
        : null;
      this.collection = data.collections?.find(
        (item) => item.season === (previewSeason || this.dataset.season)
      );
      if (!this.collection && isLocalPreview) this.collection = data.collections?.[0];
      if (!this.collection) throw new Error('Unknown collection');
      this.origin = this.dataset.assetOrigin;
      this.selectedShade = 0;
      this.selectedBrand = Object.keys(this.collection.nailArts || {})[0] || 'gelish';
      this.selectedArt = 0;
      this.selectedStep = 0;
      const seasonParts = this.collection.season.split('-');
      const seasonLabel = `${seasonParts[1]} ${seasonParts[0]}`;

      this.innerHTML = `
        <section class="color-collection-hero">
          ${picture(this.origin, this.collection.bannerCollection, `${this.collection.name} ${seasonLabel} collection`)}
        </section>
        <header class="color-collection-mobile-title">
          <h1>${escapeHtml(this.collection.name)}</h1>
          <p>${escapeHtml(seasonLabel)}<br>collection</p>
        </header>
        <section class="color-collection-products" aria-labelledby="ColorCollectionProductsTitle">
          <div class="color-collection-products__glam">${picture(this.origin, this.collection.productGlamShot, `${this.collection.name} colors`)}</div>
          <div class="color-collection-products__content">
            <header>
              <h2 id="ColorCollectionProductsTitle">${escapeHtml(seasonParts[0])} Collection Colours</h2>
              <p>${escapeHtml(this.collection.name)}</p>
            </header>
            <div class="color-collection-swatches" role="tablist" aria-label="Collection colors">
              ${(this.collection.colors || []).map((color, index) => `<button type="button" role="tab" data-shade="${index}" aria-selected="${index === 0}" style="--swatch:${escapeHtml(color)}"><span class="visually-hidden">Color ${index + 1}</span></button>`).join('')}
            </div>
            <div class="color-collection-product-grid" data-collection-product-grid></div>
          </div>
        </section>
        <section class="color-collection-story" aria-labelledby="ColorCollectionStoryTitle">
          <div class="color-collection-story__gallery">
            ${(this.collection.descriptionImages || []).map((image, index) => picture(this.origin, image, `${this.collection.name} campaign ${index + 1}`)).join('')}
          </div>
          <div class="color-collection-story__copy">
            <h2 id="ColorCollectionStoryTitle">${escapeHtml(this.collection.name)}</h2>
            <p>${escapeHtml(seasonLabel)} collection</p>
            <div>${this.collection.description || ''}</div>
          </div>
        </section>
        <div data-nail-art-region></div>
        ${this.collection.season === 'spring-2026' ? `
          <video class="color-collection-campaign-video" controls preload="metadata">
            <source src="${escapeHtml(mediaUrl(this.origin, '/videos/collection/spring_2026/spring_2026_video_landscape.mp4'))}" media="(min-width: 768px)" type="video/mp4">
            <source src="${escapeHtml(mediaUrl(this.origin, '/videos/collection/spring_2026/spring_2026_video_vertical.mp4'))}" media="(max-width: 767px)" type="video/mp4">
          </video>` : ''}`;

      this.controller?.abort();
      this.controller = new AbortController();
      this.querySelectorAll('[data-shade]').forEach((button) => button.addEventListener('click', () => {
        this.selectedShade = Number(button.dataset.shade);
        this.querySelectorAll('[data-shade]').forEach((item) => item.setAttribute('aria-selected', String(item === button)));
        this.renderProducts();
      }, { signal: this.controller.signal }));
      this.renderProducts();
      this.renderNailArt();
    }

    productForSku(sku) {
      const source = this.productNodes.get(String(sku));
      if (!source) return null;
      const clone = source.cloneNode(true);
      clone.removeAttribute('data-color-product');
      return clone;
    }

    renderProducts() {
      const grid = this.querySelector('[data-collection-product-grid]');
      if (!grid) return;
      grid.replaceChildren();
      ['gelish', 'mt', 'dip', 'mini'].forEach((brand) => {
        const sku = this.collection.skus?.[brand]?.[this.selectedShade];
        if (!sku) return;
        const card = this.productForSku(sku);
        if (card) grid.append(card);
        else {
          const missing = document.createElement('div');
          missing.className = 'color-collection-product-missing';
          missing.textContent = `Product ${sku} is not yet available.`;
          grid.append(missing);
        }
      });
    }

    renderNailArt() {
      const region = this.querySelector('[data-nail-art-region]');
      const nailArts = this.collection.nailArts;
      if (!region || !nailArts || nailArts === 'undefined' || Array.isArray(nailArts)) return;
      const brands = Object.keys(nailArts).filter((brand) => nailArts[brand]?.artContent?.length);
      if (!brands.length) return;
      if (!brands.includes(this.selectedBrand)) this.selectedBrand = brands[0];
      const arts = nailArts[this.selectedBrand];
      const art = arts.artContent[this.selectedArt] || arts.artContent[0];
      if (!art) return;
      this.selectedArt = Math.min(this.selectedArt, arts.artContent.length - 1);
      this.selectedStep = Math.min(this.selectedStep, art.steps.length - 1);
      const step = art.steps[this.selectedStep];
      const neededSkus = [...(art.youWillNeedColors || []), ...(art.youWillNeed || [])];

      region.innerHTML = `
        <section class="color-collection-nail-art" aria-labelledby="CollectionNailArtTitle">
          <header class="color-collection-nail-art__header">
            <h2 id="CollectionNailArtTitle">NAIL ART</h2>
            <p>CHOOSE YOUR FAVORITE STEP BY STEP</p>
            ${brands.length > 1 ? `<div class="color-collection-brand-tabs">${brands.map((brand) => `<button type="button" data-art-brand="${escapeHtml(brand)}" class="${brand === this.selectedBrand ? 'is-active' : ''}">${escapeHtml(brand)}</button>`).join('')}</div>` : ''}
          </header>
          <div class="color-collection-art-choices">
            ${(arts.indexArts || []).map((item, index) => `<button type="button" data-art-index="${index}" class="${index === this.selectedArt ? 'is-active' : ''}">${picture(this.origin, item.fingerimage, item.name)}<span>${escapeHtml(item.name)}</span></button>`).join('')}
          </div>
          <div class="color-collection-art-step">
            <button type="button" data-step-direction="-1" aria-label="Previous step">‹</button>
            <div class="color-collection-art-step__copy">
              <h3>${art.title || ''}</h3>
              <p>${step.description || ''}</p>
            </div>
            <div class="color-collection-art-step__image">${picture(this.origin, step.image, `${art.title} step ${this.selectedStep + 1}`)}</div>
            <button type="button" data-step-direction="1" aria-label="Next step">›</button>
          </div>
          <div class="color-collection-needed">
            <header><div><span>For this look</span><h3>YOU WILL NEED</h3></div><button type="button" data-add-all>ADD ALL TO BAG</button></header>
            <div class="color-collection-needed__products" data-needed-products></div>
          </div>
        </section>`;

      const needed = region.querySelector('[data-needed-products]');
      neededSkus.forEach((sku) => {
        const card = this.productForSku(sku);
        if (card) needed.append(card);
      });
      region.querySelectorAll('[data-art-brand]').forEach((button) => button.addEventListener('click', () => {
        this.selectedBrand = button.dataset.artBrand;
        this.selectedArt = 0;
        this.selectedStep = 0;
        this.renderNailArt();
      }));
      region.querySelectorAll('[data-art-index]').forEach((button) => button.addEventListener('click', () => {
        this.selectedArt = Number(button.dataset.artIndex);
        this.selectedStep = 0;
        this.renderNailArt();
      }));
      region.querySelectorAll('[data-step-index]').forEach((button) => button.addEventListener('click', () => {
        this.selectedStep = Number(button.dataset.stepIndex);
        this.renderNailArt();
      }));
      region.querySelectorAll('[data-step-direction]').forEach((button) => button.addEventListener('click', () => {
        const direction = Number(button.dataset.stepDirection);
        this.selectedStep = (this.selectedStep + direction + art.steps.length) % art.steps.length;
        this.renderNailArt();
      }));
      region.querySelector('[data-add-all]')?.addEventListener('click', (event) => this.addAll(event.currentTarget, neededSkus));
    }

    async addAll(button, skus) {
      const items = skus.map((sku) => {
        const node = this.productNodes.get(String(sku));
        return Number(node?.dataset.variantId || 0);
      }).filter(Boolean).map((id) => ({ id, quantity: 1 }));
      if (!items.length || button.disabled) return;
      button.disabled = true;
      const original = button.textContent;
      button.textContent = 'ADDING…';
      try {
        const response = await fetch(`${this.dataset.rootUrl || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!response.ok) throw new Error('Products could not be added');
        const cartResponse = await fetch(`${this.dataset.rootUrl || '/'}cart.js`, { headers: { Accept: 'application/json' } });
        const cart = cartResponse.ok ? await cartResponse.json() : null;
        document.dispatchEvent(new CustomEvent('theme:cart-updated', { detail: { cart } }));
        document.dispatchEvent(new CustomEvent('theme:toast', { detail: { message: 'Products added to your shopping bag.', type: 'success' } }));
      } catch (error) {
        document.dispatchEvent(new CustomEvent('theme:toast', { detail: { message: error.message, type: 'error' } }));
      } finally {
        button.disabled = false;
        button.textContent = original;
      }
    }
  }

  if (!customElements.get('color-collections-index')) customElements.define('color-collections-index', ColorCollectionsIndex);
  if (!customElements.get('color-collection-page')) customElements.define('color-collection-page', ColorCollectionPage);
})();
